// Tasks — full-page version of the Home "Tasks" card (design bundle: tasks.jsx
// TasksPage). Active + Completed tabs, type-filter pills, full row list. Active
// rows open the shared TaskDrawer; offboarding rows show a done/total ring.
// Completed rows show the outcome badge + close time (read-only — the detail
// endpoint serves pending tasks only, so completed rows don't open the drawer).

import { Box, Flex, Heading, HStack, Icon, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { TbArrowRight, TbCheck, TbChecklist, TbX } from "react-icons/tb";

import Chip from "components/ds/Chip";
import EmptyState from "components/ds/EmptyState";
import FilterPill from "components/ds/FilterPill";
import SearchInput from "components/ds/SearchInput";
import Tabs from "components/ds/Tabs";
import ProgressPill from "components/tasks/ProgressPill";
import TaskActions from "components/tasks/TaskActions";
import TaskDrawer from "components/tasks/TaskDrawer";
import TaskSubjectAvatar from "components/tasks/TaskSubjectAvatar";
import {
  completedResolutionMeta,
  isActionableTask,
  taskFilterCategory,
  taskRowKey,
  timeAgo,
  typeMeta,
  type TaskFilterKey,
} from "components/tasks/taskMeta";
import { MF_TASKS, type MFTaskItem } from "state/members/tasks";

type TabKey = "active" | "completed";
type PillKey = TaskFilterKey | "all";

const PILLS: { key: PillKey; label: string; color?: string }[] = [
  { key: "all", label: "All" },
  { key: "access_approval", label: "Access Approval", color: "accents.royal-purple" },
  { key: "deprovisioning", label: "Deprovisioning", color: "error.500" },
  { key: "provisioning", label: "Provisioning", color: "success.500" },
  { key: "offboarding", label: "Offboarding", color: "warning.500" },
  { key: "generic", label: "Generic", color: "p.500" },
];

const TypeCell = ({ entityType }: { entityType: string }) => {
  const meta = typeMeta(entityType);
  return (
    <HStack spacing="sm" w="160px" flexShrink={0}>
      <Box w="12px" h="8px" borderRadius="full" bg={meta.color} flexShrink={0} />
      <Box as="span" fontSize="smd" fontWeight={500} color="neutral.900" noOfLines={1}>
        {meta.label}
      </Box>
    </HStack>
  );
};

const Subject = ({ task }: { task: MFTaskItem }) => (
  <Box flex="1" minW={0}>
    <Text fontSize="md" fontWeight={500} color="neutral.950" noOfLines={1}>
      {task.title}
    </Text>
    {task.body && (
      <Text fontSize="smd" color="neutral.700" noOfLines={1}>
        {task.body}
      </Text>
    )}
  </Box>
);

const TimeCell = ({ value }: { value?: string | null }) => (
  <Box w="64px" textAlign="right" flexShrink={0} fontSize="smd" color="neutral.600">
    {timeAgo(value)}
  </Box>
);

const ROW_PROPS = {
  align: "center" as const,
  gap: "lg" as const,
  p: "lg" as const,
};

const ActiveRow = ({ task, onOpen }: { task: MFTaskItem; onOpen: () => void }) => {
  const actionable = isActionableTask(task);
  const hasProgress =
    task.taskType === "offboarding" && typeof task.accountCount === "number";

  return (
    <Flex
      {...ROW_PROPS}
      cursor="pointer"
      transition="background-color 120ms"
      _hover={{ bg: "neutral.50" }}
      onClick={onOpen}
    >
      <TypeCell entityType={task.entityType} />
      <TaskSubjectAvatar task={task} />
      <Subject task={task} />

      {hasProgress && (
        <ProgressPill done={task.accountsDone ?? 0} total={task.accountCount ?? 0} />
      )}

      <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
        {actionable ? (
          <TaskActions task={task} />
        ) : (
          <Box
            as="button"
            type="button"
            onClick={onOpen}
            display="inline-flex"
            alignItems="center"
            gap="2xs"
            px="10px"
            py="1xs"
            fontSize="smd"
            fontWeight={500}
            color="p.600"
            bg="neutral.25"
            borderRadius="component.lg"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="neutral.200"
            cursor="pointer"
            transition="background-color 120ms, border-color 120ms"
            _hover={{ bg: "neutral.50", borderColor: "neutral.300" }}
          >
            Review
            <Icon as={TbArrowRight} boxSize="14px" />
          </Box>
        )}
      </Box>

      <TimeCell value={task.createdAt} />
    </Flex>
  );
};

const CompletedRow = ({ task, onOpen }: { task: MFTaskItem; onOpen: () => void }) => {
  const res = completedResolutionMeta(task.result, task.entityType);
  return (
    <Flex
      {...ROW_PROPS}
      opacity={0.9}
      cursor="pointer"
      transition="background-color 120ms, opacity 120ms"
      _hover={{ bg: "neutral.50", opacity: 1 }}
      onClick={onOpen}
    >
      <TypeCell entityType={task.entityType} />
      <TaskSubjectAvatar task={task} />
      <Subject task={task} />
      <Box flexShrink={0}>
        <Chip
          tone={res.tone}
          icon={<Icon as={res.positive ? TbCheck : TbX} boxSize="12px" />}
        >
          {res.label}
        </Chip>
      </Box>
      <TimeCell value={task.completedAt} />
    </Flex>
  );
};

const ListCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    borderWidth="1px"
    borderStyle="solid"
    borderColor="neutral.200"
    borderRadius="component.2xl"
    bg="neutral.25"
    p="sm"
  >
    {children}
  </Box>
);

const withDividers = (rows: React.ReactNode[]) =>
  rows.map((row, i) => (
    <Box key={i}>
      {row}
      {i < rows.length - 1 && <Box h="1px" bg="neutral.100" mx="lg" />}
    </Box>
  ));

const TasksPage = () => {
  const active = MF_TASKS.useListTasks();
  const completed = MF_TASKS.useListCompletedTasks();
  const [tab, setTab] = useState<TabKey>("active");
  const [filter, setFilter] = useState<PillKey>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MFTaskItem | null>(null);

  const source = tab === "active" ? active.data : completed.data;

  const counts = useMemo(() => {
    const c: Record<PillKey, number> = {
      all: source.length,
      access_approval: 0,
      deprovisioning: 0,
      provisioning: 0,
      offboarding: 0,
      generic: 0,
    };
    source.forEach((t) => {
      c[taskFilterCategory(t.entityType)] += 1;
    });
    return c;
  }, [source]);

  const visible = useMemo(() => {
    let list =
      filter === "all"
        ? source
        : source.filter((t) => taskFilterCategory(t.entityType) === filter);
    const q = search.trim().toLowerCase();
    if (tab === "completed" && q) {
      list = list.filter(
        (t) =>
          t.title?.toLowerCase().includes(q) ||
          t.memberName?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [source, filter, search, tab]);

  return (
    <Box maxW="1440px" mx="auto" w="full">
      <Flex align="center" py="lg">
        <Heading
          as="h1"
          fontFamily="heading"
          fontWeight={500}
          fontSize="3xl"
          letterSpacing="-0.01em"
          lineHeight="1"
          color="neutral.950"
        >
          Tasks
        </Heading>
      </Flex>

      <Tabs<TabKey>
        tabs={[
          { key: "active", label: "Active", count: active.data.length },
          { key: "completed", label: "Completed", count: completed.data.length },
        ]}
        activeKey={tab}
        onChange={(k) => {
          setTab(k);
          setFilter("all");
        }}
      />

      {tab === "completed" && (
        <Box maxW="320px" mb="lg">
          <SearchInput value={search} onChange={setSearch} placeholder="Search completed tasks…" />
        </Box>
      )}

      <Flex align="center" gap="sm" flexWrap="wrap" mb="20px">
        <Box as="span" fontSize="smd" color="neutral.700" mr="2xs">
          Types
        </Box>
        {PILLS.map((p) => (
          <FilterPill
            key={p.key}
            dotColor={p.color}
            count={counts[p.key]}
            active={filter === p.key}
            onClick={() => setFilter(p.key)}
          >
            {p.label}
          </FilterPill>
        ))}
      </Flex>

      <ListCard>
        {tab === "active" ? (
          active.isLoading ? (
            <Box py="3xl" />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<TbChecklist />}
              title="No active tasks"
              body="You're caught up. New requests will appear here as your team submits them."
            />
          ) : (
            withDividers(
              visible.map((task, i) => (
                <ActiveRow key={taskRowKey(task, i)} task={task} onOpen={() => setSelected(task)} />
              )),
            )
          )
        ) : completed.isLoading ? (
          <Box py="3xl" />
        ) : visible.length === 0 ? (
          <EmptyState
            title="No completed tasks yet"
            body="Tasks you've approved, denied, or finished will appear here."
          />
        ) : (
          withDividers(
            visible.map((task, i) => (
              <CompletedRow key={taskRowKey(task, i)} task={task} onOpen={() => setSelected(task)} />
            )),
          )
        )}
      </ListCard>

      <TaskDrawer task={selected} onClose={() => setSelected(null)} />
    </Box>
  );
};

export default TasksPage;
